import { api } from "@/features/utils/fetch-server";
import type {
  CallingCode,
  Connector,
  CreateConnectorData,
  UpdateConnectorData,
  TestConnectorResult,
  ConnectorType,
  ConnectorStatus,
  HeyupNumbers,
} from "../types";

export class ConnectorsAPI {
  /**
   * Get all connectors for a client
   * Can filter by type and status
   */
  static getConnectors = async (
    clientId: string,
    filters?: {
      type?: ConnectorType;
      status?: ConnectorStatus;
    },
  ): Promise<Connector[]> => {
    const params = new URLSearchParams();
    if (filters?.type) params.append("type", filters.type);
    if (filters?.status) params.append("status", filters.status);

    const queryString = params.toString();
    const url = `/api/sessions/v1/${clientId}/connectors${queryString ? `?${queryString}` : ""}`;

    return api.get<Connector[]>(url);
  };

  /**
   * The E.164 calling-code table, for the outbound-country picker.
   *
   * Served by the backend rather than shipped as an asset: the outbound filter stores
   * calling-code prefixes, so a picker offering "Belgique" has to know it means "+32",
   * and a second copy of 200 entries here is a second copy that drifts from the one the
   * filter actually matches on.
   */
  static getCallingCodes = async (clientId: string): Promise<CallingCode[]> => {
    const result = await api.get<{ calling_codes: CallingCode[] }>(
      `/api/sessions/v1/${clientId}/connectors/calling-codes`,
    );
    return result.calling_codes || [];
  };

  /**
   * The HeyUp numbers this account may answer on.
   *
   * A "Numéro HeyUp" channel offers a selector rather than a text field: the
   * line runs on HeyUp's own carrier account, so the only numbers that can be
   * attached are the ones we attributed on the account's number requests.
   */
  static getHeyupNumbers = async (clientId: string): Promise<HeyupNumbers> => {
    return api.get<HeyupNumbers>(
      `/api/sessions/v1/${clientId}/connectors/heyup-numbers`,
    );
  };

  /**
   * Get a specific connector by ID
   */
  static getConnector = async (
    clientId: string,
    connectorId: string,
  ): Promise<Connector> => {
    return api.get<Connector>(
      `/api/sessions/v1/${clientId}/connectors/${connectorId}`,
    );
  };

  /**
   * Create a new connector
   */
  static createConnector = async (
    clientId: string,
    data: CreateConnectorData,
  ): Promise<Connector> => {
    return api.post<Connector, CreateConnectorData>(
      `/api/sessions/v1/${clientId}/connectors`,
      data,
    );
  };

  /**
   * Update a connector (full replacement)
   */
  static updateConnector = async (
    clientId: string,
    connectorId: string,
    data: UpdateConnectorData,
  ): Promise<Connector> => {
    return api.put<Connector, UpdateConnectorData>(
      `/api/sessions/v1/${clientId}/connectors/${connectorId}`,
      data,
    );
  };

  /**
   * Patch a connector (partial update, merges configuration)
   */
  static patchConnector = async (
    clientId: string,
    connectorId: string,
    data: Partial<UpdateConnectorData>,
  ): Promise<Connector> =>
    api.patch(`/api/sessions/v1/${clientId}/connectors/${connectorId}`, data);

  /**
   * Delete a connector
   */
  static deleteConnector = async (
    clientId: string,
    connectorId: string,
  ): Promise<{ success: boolean }> => {
    return api.delete<{ success: boolean }>(
      `/api/sessions/v1/${clientId}/connectors/${connectorId}`,
    );
  };

  /**
   * Test a connector's configuration
   */
  static testConnector = async (
    clientId: string,
    connectorId: string,
  ): Promise<TestConnectorResult> => {
    return api.post<TestConnectorResult, undefined>(
      `/api/sessions/v1/${clientId}/connectors/${connectorId}/test`,
    );
  };
}
